import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Cog, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusColor = (s) => ({
  Available: 'bg-green-100 text-green-700', 'In Repair': 'bg-amber-100 text-amber-700', Decommissioned: 'bg-gray-200 text-gray-600',
}[s] || 'bg-gray-100');
const EMPTY = { description: '', equipment_category: 'Machine', functional_location: '', manufacturer: '', model_number: '', serial_number: '', main_work_center: '', cost_center: '', construction_year: '', status: 'Available' };

const Equipment = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('equipment');
  const [rows, setRows] = useState([]);
  const [flocs, setFlocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/pm/equipment`, { withCredentials: true }); setRows(res.data); }
    catch (e) { toast.error('Failed to load equipment'); }
    finally { setLoading(false); }
  }, []);
  const loadFlocs = useCallback(async () => {
    try { const res = await axios.get(`${API}/pm/functional-locations`, { withCredentials: true }); setFlocs(res.data); }
    catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); loadFlocs(); }, [load, loadFlocs]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/pm/equipment`, {
        ...form,
        functional_location: form.functional_location || null,
        construction_year: form.construction_year === '' ? null : parseInt(form.construction_year, 10),
      }, { withCredentials: true });
      toast.success('Equipment created'); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="equipment-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sky-100 text-sky-700"><Cog className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Equipment</h1>
              <p className="text-gray-600 text-sm mt-0.5">Machines, vehicles and technical assets.</p>
            </div>
          </div>
          {writable && <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-equipment-btn"><Plus className="h-4 w-4 mr-2" />New Equipment</Button>}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Equipment #</TableHead><TableHead>Description</TableHead><TableHead>Category</TableHead>
              <TableHead>Location</TableHead><TableHead>Manufacturer</TableHead><TableHead>Work Center</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                : rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No equipment yet.</TableCell></TableRow>
                : rows.map((r) => (
                  <TableRow key={r.equipment_number} data-testid={`equipment-row-${r.equipment_number}`}>
                    <TableCell className="font-medium">{r.equipment_number}</TableCell>
                    <TableCell>{r.description}</TableCell>
                    <TableCell>{r.equipment_category}</TableCell>
                    <TableCell>{r.functional_location || '—'}</TableCell>
                    <TableCell>{r.manufacturer || '—'}</TableCell>
                    <TableCell>{r.main_work_center || '—'}</TableCell>
                    <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${statusColor(r.status)}`}>{r.status}</span></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Equipment</DialogTitle>
            <DialogDescription>Register a piece of equipment and its installation location.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label>Description *</Label><Input data-testid="equipment-description" value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.equipment_category} onValueChange={(v) => set('equipment_category', v)}>
                <SelectTrigger data-testid="equipment-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Machine">Machine</SelectItem>
                  <SelectItem value="Vehicle">Vehicle</SelectItem>
                  <SelectItem value="Production Resource">Production Resource</SelectItem>
                  <SelectItem value="Instrument">Instrument</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Functional Location</Label>
              <Select value={form.functional_location || 'none'} onValueChange={(v) => set('functional_location', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="equipment-floc"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {flocs.map((f) => <SelectItem key={f.functional_location} value={f.functional_location}>{f.functional_location} — {f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Manufacturer</Label><Input data-testid="equipment-manufacturer" value={form.manufacturer} onChange={(e) => set('manufacturer', e.target.value)} /></div>
            <div><Label>Model</Label><Input data-testid="equipment-model" value={form.model_number} onChange={(e) => set('model_number', e.target.value)} /></div>
            <div><Label>Serial Number</Label><Input data-testid="equipment-serial" value={form.serial_number} onChange={(e) => set('serial_number', e.target.value)} /></div>
            <div><Label>Main Work Center</Label><Input data-testid="equipment-workcenter" value={form.main_work_center} onChange={(e) => set('main_work_center', e.target.value)} /></div>
            <div><Label>Construction Year</Label><Input type="number" data-testid="equipment-year" value={form.construction_year} onChange={(e) => set('construction_year', e.target.value)} /></div>
            <div><Label>Cost Center</Label><Input data-testid="equipment-costcenter" value={form.cost_center} onChange={(e) => set('cost_center', e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.description} data-testid="save-equipment-btn">{saving ? 'Saving...' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Equipment;
